import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Mail, ShieldCheck, Eye } from "lucide-react";
import { toast } from "sonner";
import { SEGMENTS, TIERS, INTERESTS, subscribeNewsletter, listIssues } from "../lib/newsletter";
import { formatApiErrorDetail } from "../lib/api";
import BriefingCTABlock from "../components/BriefingCTABlock";

const PILLARS = [
  { kicker: "WEALTH & FAMILY", body: "Private markets, governance, estate planning, tax strategies, succession." },
  { kicker: "MARKET & CAPITAL", body: "Macro outlook, NYSE/Nasdaq themes, private equity, REITs, commodities, metals." },
  { kicker: "LUXURY & ASSETS", body: "Watches, jewelry, collectible cars, art, diamonds, yachts, hospitality." },
  { kicker: "POLICY & TAX", body: "Federal and state tax liens, international tax developments, regulatory shifts." },
  { kicker: "DEAL FLOW & SIGNALS", body: "M&A, LBOs, luxury-boutique transactions, family-office adjacencies." },
];

export default function Newsletter() {
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    listIssues().then((data) => setRecent(data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="newsletter-page" className="newsletter-theme pt-24">
      {/* HERO */}
      <section className="px-6 pt-12 pb-20" style={{ background: "var(--bg)" }}>
        <div className="max-w-5xl mx-auto">
          <p className="newsletter-eyebrow mb-5">WEALTH FIRST · PRIVATE INTELLIGENCE</p>
          <h1 className="newsletter-display leading-[0.95] text-5xl md:text-7xl mb-7">
            Where affluent audiences meet
            <em style={{ color: "var(--brass)" }}> luxury brands</em>, private capital, and
            <em style={{ color: "var(--brass)" }}> curated market intelligence</em>.
          </h1>
          <p className="newsletter-body max-w-3xl text-base md:text-lg mb-10" style={{ color: "var(--ink-muted)" }}>
            A monthly briefing for UHNW earners, family offices, accreditation institutions, and the
            luxury market — built around signal over noise. We separate what matters from what is loud.
          </p>
          <a href="#subscribe" className="btn-brass" data-testid="newsletter-cta-hero">
            Access the Briefing <ArrowRight size={14} />
          </a>
        </div>
      </section>

      {/* PILLARS */}
      <section className="px-6 py-24">
        <div className="max-w-6xl mx-auto">
          <p className="newsletter-eyebrow mb-4">Editorial Pillars</p>
          <h2 className="newsletter-display text-4xl md:text-5xl mb-14 max-w-3xl">
            Five lenses on the <em style={{ color: "var(--brass)" }}>quiet markets</em> that move wealth.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map((p) => (
              <div key={p.kicker} className="p-7" style={{ border: "1px solid var(--hairline)" }}>
                <p className="newsletter-eyebrow mb-3" style={{ color: "var(--brass)" }}>{p.kicker}</p>
                <p className="newsletter-body text-sm leading-relaxed" style={{ color: "var(--ink)" }}>
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MONTHLY STRUCTURE */}
      <section className="px-6 py-24" style={{ background: "var(--surface)" }}>
        <div className="max-w-5xl mx-auto">
          <p className="newsletter-eyebrow mb-4">Monthly Structure</p>
          <h2 className="newsletter-display text-4xl md:text-5xl mb-14 max-w-3xl">
            Designed like a <em style={{ color: "var(--brass)" }}>private briefing</em>, not a digest.
          </h2>
          <ol className="space-y-6 max-w-3xl">
            {[
              ["Cover Brief", "One-page editor's note on the month's most relevant theme."],
              ["Market Pulse", "Public & private market snapshot, rates, commodities, capital markets."],
              ["Family Office Desk", "Tax, governance, private deal flow, alternative assets."],
              ["Luxury Intelligence", "Watches, cars, fashion, diamonds, travel, hospitality."],
              ["State & International Tax Liens", "State-by-state and notable cross-border items."],
              ["Private Signals", "What insiders are watching — confidential-feeling notes."],
            ].map(([t, b], i) => (
              <li key={t} className="flex gap-6">
                <span className="newsletter-display text-3xl shrink-0" style={{ color: "var(--brass)", minWidth: 56 }}>
                  0{i + 1}
                </span>
                <div>
                  <h3 className="newsletter-display text-2xl mb-1.5" style={{ color: "var(--ink)" }}>{t}</h3>
                  <p className="newsletter-body text-base leading-relaxed" style={{ color: "var(--ink-muted)" }}>{b}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* RECENT ISSUES TEASER */}
      {recent.length > 0 && (
        <section className="px-6 py-24">
          <div className="max-w-6xl mx-auto">
            <p className="newsletter-eyebrow mb-4">Recent Briefings</p>
            <h2 className="newsletter-display text-4xl md:text-5xl mb-14 max-w-3xl">
              The most recent monthly issues.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {recent.map((iss) => (
                <Link
                  key={iss.id}
                  to={`/newsletter/${iss.slug}`}
                  className="p-7 block transition-colors"
                  style={{ border: "1px solid var(--hairline)", textDecoration: "none" }}
                  data-testid={`newsletter-issue-card-${iss.slug}`}
                >
                  <p className="newsletter-eyebrow mb-3">{iss.month_label}</p>
                  <h3 className="newsletter-display text-2xl mb-3 leading-tight" style={{ color: "var(--ink)" }}>
                    {iss.headline || iss.theme}
                  </h3>
                  <p className="newsletter-body text-sm" style={{ color: "var(--ink-muted)" }}>
                    {iss.subtitle || "Wealth First Private Intelligence."}
                  </p>
                  <p className="newsletter-eyebrow mt-5 inline-flex items-center gap-2" style={{ color: "var(--brass)" }}>
                    Read the briefing <ArrowRight size={12} />
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PREMIUM CTA BLOCK — per wireframe spec */}
      <BriefingCTABlock
        sectionLabel="Monthly Briefing"
        headline="Access this month's private signals."
        supportingCopy="Curated for UHNW earners, family offices, advisors, and luxury-market partners — quiet signal, not noise."
        primaryHref="/newsletter#subscribe"
        primaryLabel="Access the Full Briefing"
        secondaryPrompt="Prefer a tailored partnership note?"
        secondaryHref="/contact?type=media-kit"
        secondaryLabel="Request the Media Kit"
      />

      {/* SUBSCRIBE */}
      <section id="subscribe" className="px-6 py-24" style={{ background: "var(--bg)" }}>
        <div className="max-w-3xl mx-auto">
          <SubscribeForm />
        </div>
      </section>

      {/* QUIET FOOTER */}
      <section className="px-6 py-16">
        <div className="max-w-3xl mx-auto text-center">
          <Sparkles size={20} style={{ color: "var(--brass)", margin: "0 auto 1rem" }} />
          <p className="newsletter-body text-sm" style={{ color: "var(--ink-muted)" }}>
            <em>WealthFirst moves quietly.</em> Each issue is curated by the Editorial Desk. No discounts. No noise.
            Reply to any issue with the topics you want covered next.
          </p>
        </div>
      </section>
    </div>
  );
}

function SubscribeForm() {
  const [form, setForm] = useState({
    email: "",
    name: "",
    segment: "uhnw",
    tier: "premium",
    interests: [],
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleInterest = (i) =>
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await subscribeNewsletter(form);
      setDone(true);
      toast.success("Welcome to the Editorial Desk.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not subscribe.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="newsletter-subscribed" className="text-center">
        <ShieldCheck size={48} style={{ color: "var(--brass)", margin: "0 auto 1.5rem" }} />
        <p className="newsletter-eyebrow mb-4">Welcome</p>
        <h2 className="newsletter-display text-4xl md:text-5xl mb-6 leading-[0.95]">
          You are on the <em style={{ color: "var(--brass)" }}>private list</em>.
        </h2>
        <p className="newsletter-body text-base" style={{ color: "var(--ink-muted)" }}>
          The next monthly briefing arrives at <span style={{ color: "var(--brass)" }}>{form.email}</span>.
          Reply to that issue with the topics you want covered.
        </p>
      </div>
    );
  }

  return (
    <div>
      <p className="newsletter-eyebrow mb-4">Subscribe</p>
      <h2 className="newsletter-display text-4xl md:text-5xl mb-4 leading-[0.95]">
        Access the monthly <em style={{ color: "var(--brass)" }}>briefing</em>.
      </h2>
      <p className="newsletter-body text-base mb-10" style={{ color: "var(--ink-muted)" }}>
        Select your edition. We segment by reader so the content arrives relevant.
      </p>

      <form onSubmit={submit} className="space-y-6">
        <div>
          <label className="newsletter-eyebrow block mb-2">Email *</label>
          <input
            required
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            className="input-line"
            data-testid="newsletter-email"
            autoComplete="email"
          />
        </div>
        <div>
          <label className="newsletter-eyebrow block mb-2">Name (optional)</label>
          <input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            className="input-line"
            data-testid="newsletter-name"
          />
        </div>

        <div>
          <label className="newsletter-eyebrow block mb-3">Reader Segment</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="newsletter-segments">
            {SEGMENTS.map((s) => {
              const active = form.segment === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => set("segment", s.id)}
                  data-testid={`newsletter-segment-${s.id}`}
                  className="text-left px-4 py-3 text-sm transition-colors"
                  style={{
                    border: `1px solid ${active ? "var(--brass)" : "var(--hairline-strong)"}`,
                    background: active ? "rgba(194,163,115,0.08)" : "transparent",
                    color: active ? "var(--brass)" : "var(--ink)",
                  }}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="newsletter-eyebrow block mb-3">Edition</label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="newsletter-tiers">
            {TIERS.map((t) => {
              const active = form.tier === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => set("tier", t.id)}
                  data-testid={`newsletter-tier-${t.id}`}
                  className="text-left p-4 transition-colors"
                  style={{
                    border: `1px solid ${active ? "var(--brass)" : "var(--hairline-strong)"}`,
                    background: active ? "rgba(194,163,115,0.08)" : "transparent",
                  }}
                >
                  <p className="newsletter-eyebrow mb-1.5" style={{ color: active ? "var(--brass)" : "var(--ink-muted)" }}>
                    {t.label}
                  </p>
                  <p className="newsletter-body text-sm mb-2" style={{ color: "var(--ink)" }}>{t.summary}</p>
                  <p className="newsletter-body text-xs" style={{ color: "var(--ink-muted)" }}>{t.blurb}</p>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="newsletter-eyebrow block mb-3">Interests (optional)</label>
          <div className="flex flex-wrap gap-2">
            {INTERESTS.map((i) => {
              const active = form.interests.includes(i);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => toggleInterest(i)}
                  data-testid={`newsletter-interest-${i.replace(/\W/g, "-").toLowerCase()}`}
                  className="text-xs uppercase tracking-[0.1em] px-3 py-1.5 transition-colors"
                  style={{
                    border: `1px solid ${active ? "var(--brass)" : "var(--hairline-strong)"}`,
                    background: active ? "rgba(194,163,115,0.08)" : "transparent",
                    color: active ? "var(--brass)" : "var(--ink-muted)",
                  }}
                >
                  {i}
                </button>
              );
            })}
          </div>
        </div>

        <button
          type="submit"
          disabled={busy}
          className="btn-brass w-full justify-center"
          data-testid="newsletter-submit"
          style={{ minHeight: 48 }}
        >
          {busy ? "Subscribing…" : "Access the Briefing"}
          <ArrowRight size={14} />
        </button>
        <p className="newsletter-body text-xs text-center" style={{ color: "var(--ink-muted)" }}>
          <Eye size={11} style={{ display: "inline", marginRight: 6 }} />
          We honor discretion. Reply with the topics you want covered. Unsubscribe anytime.
        </p>
      </form>
    </div>
  );
}
